/**
 * 약관 관리 API (어드민)
 *
 * - GET    /api/admin/terms
 * - GET    /api/admin/terms/history?targetRole=&termsType=
 * - GET    /api/admin/terms/{id}
 * - POST   /api/admin/terms          ← 개정 = 항상 신규 버전 등록
 * - PUT    /api/admin/terms/{id}     ← 시행 전 버전만 수정 가능
 * - DELETE /api/admin/terms/{id}     ← 시행 전 버전만 삭제 가능
 *
 * 핵심 규칙: 시행된 버전은 동의 증빙의 원본이라 수정·삭제가 막혀 있다.
 * 문구를 바꾸려면 새 버전을 등록해야 한다.
 */
import { apiClient } from './client';

export type TermsTargetRole = 'COMMON' | 'GUARDIAN' | 'BUSINESS';

export type TermsType =
  | 'TERMS_TYPE_SERVICE'
  | 'TERMS_TYPE_PRIVACY'
  | 'TERMS_TYPE_LOCATION'
  | 'TERMS_TYPE_MARKETING';

/** DT_EFFECTIVE 기준 계산값 (DB 미저장) */
export type TermsStatus = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'ARCHIVED';

export interface Terms {
  id: number;
  targetRole: TermsTargetRole;
  termsType: TermsType;
  termsName?: string | null;
  termsTitle: string;
  termsVersion: string;
  /** 목록 응답에서는 null (본문이 커서 제외됨) */
  contentHtml?: string | null;
  termsUrl?: string | null;
  changeSummary?: string | null;
  isRequired: boolean;
  status: TermsStatus;
  /** 상세 조회에서만 채워짐 */
  agreedCount?: number | null;
  effectiveAt?: string | null;
  noticeAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
}

export interface TermsCreateInput {
  targetRole: TermsTargetRole;
  termsType: TermsType;
  termsName?: string;
  termsTitle: string;
  termsVersion: string;
  contentHtml: string;
  termsUrl?: string;
  effectiveAt?: string | null;
  noticeAt?: string | null;
  changeSummary?: string;
  isRequired?: boolean;
}

export interface TermsUpdateInput {
  termsName?: string;
  termsTitle?: string;
  termsVersion?: string;
  contentHtml?: string;
  termsUrl?: string;
  effectiveAt?: string | null;
  noticeAt?: string | null;
  clearEffectiveAt?: boolean;
  clearNoticeAt?: boolean;
  changeSummary?: string;
  isRequired?: boolean;
}

export interface GetTermsParams {
  page?: number;
  size?: number;
  targetRole?: TermsTargetRole | 'ALL';
  termsType?: TermsType | 'ALL';
}

export interface TermsPage {
  content: Terms[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export const TERMS_TARGET_LABEL: Record<TermsTargetRole, string> = {
  COMMON: '공통',
  GUARDIAN: '보호자',
  BUSINESS: '사업자',
};

export const TERMS_TYPE_LABEL: Record<TermsType, string> = {
  TERMS_TYPE_SERVICE: '이용약관',
  TERMS_TYPE_PRIVACY: '개인정보처리방침',
  TERMS_TYPE_LOCATION: '위치기반서비스',
  TERMS_TYPE_MARKETING: '마케팅 수신동의',
};

export const TERMS_STATUS_LABEL: Record<TermsStatus, string> = {
  DRAFT: '임시저장',
  SCHEDULED: '시행 예정',
  ACTIVE: '현행',
  ARCHIVED: '이전 버전',
};

const BASE = '/admin/terms';

export const termsApi = {
  getTermsList: async (params: GetTermsParams = {}): Promise<TermsPage> => {
    const res = await apiClient.get<TermsPage>(BASE, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 10,
        targetRole:
          params.targetRole && params.targetRole !== 'ALL'
            ? params.targetRole
            : undefined,
        termsType:
          params.termsType && params.termsType !== 'ALL'
            ? params.termsType
            : undefined,
      },
    });
    return res.data;
  },

  /** 특정 대상·유형의 전체 버전 이력 (시행일 내림차순, 페이징 없음) */
  getVersionHistory: async (
    targetRole: TermsTargetRole,
    termsType: TermsType,
  ): Promise<Terms[]> => {
    const res = await apiClient.get<Terms[]>(`${BASE}/history`, {
      params: { targetRole, termsType },
    });
    return res.data;
  },

  getTermsDetail: async (id: number): Promise<Terms> => {
    const res = await apiClient.get<Terms>(`${BASE}/${id}`);
    return res.data;
  },

  createTerms: async (input: TermsCreateInput): Promise<Terms> => {
    const res = await apiClient.post<Terms>(BASE, input);
    return res.data;
  },

  updateTerms: async (id: number, input: TermsUpdateInput): Promise<Terms> => {
    const res = await apiClient.put<Terms>(`${BASE}/${id}`, input);
    return res.data;
  },

  deleteTerms: async (id: number): Promise<void> => {
    await apiClient.delete<void>(`${BASE}/${id}`);
  },
};
